import InfiniteScroll from "react-infinite-scroll-component";
import styled from "@emotion/styled";
import { Skeleton } from "antd5";

import Button from "components/ui/button/Button";
import CentredSpinner from "../../../lib/core_components/CentredSpinner";
import { LeadFromSearch } from "../../../lib/hooks/api/leads/useLeads";
import { smoothTransition } from "../../../styles/animations";
import { Flex, Text } from "../../../styles/utility-components";
import EmptyState from "../../ui/empty_state/EmptyState";

export const VIEW_LEAD_LIST_CONTAINER_ID = "view-lead-list";

type Props = {
  isLoading: boolean;
  isError: boolean;
  leads: LeadFromSearch[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
};

export function ViewLeadsList({ isLoading, isError, leads, fetchNextPage, hasNextPage }: Props) {
  if (isLoading) {
    return <Skeleton active />;
  }

  if (isError) {
    return (
      <EmptyState
        type="loadError"
        heading="Sorry something has gone wrong loading leads"
        description="If this error persists please contact support"
        width="100%"
        size="large"
      />
    );
  }

  if (leads.length === 0) {
    return (
      <EmptyState
        heading="No leads found"
        description="There are no leads to display."
        width="100%"
        size="large"
      />
    );
  }

  return (
    <LeadsListContainer
      dataLength={leads.length}
      next={fetchNextPage}
      hasMore={hasNextPage || false}
      loader={<CentredSpinner />}
      scrollableTarget={VIEW_LEAD_LIST_CONTAINER_ID}
    >
      <Flex column gap={16}>
        {leads.map((lead) => (
          <ViewLeadListItem key={lead.id} lead={lead} />
        ))}
      </Flex>
    </LeadsListContainer>
  );
}

function ViewLeadListItem({ lead }: { lead: LeadFromSearch }) {
  return (
    <LeadItem key={lead.id} aria-label={lead.name}>
      <ContentFlex>
        <Flex column minWidth={0}>
          <Text ellipsis>{lead.name}</Text>
          <Text description ellipsis color="sysTextSecondary">
            {lead.description}
          </Text>
        </Flex>
      </ContentFlex>
      <ActionsContainer>
        <Button as="a" buttonType="secondary" to="/leads/$id" params={{ id: lead.id }}>
          View
        </Button>
      </ActionsContainer>
    </LeadItem>
  );
}

const ActionsContainer = styled.div({
  display: "none",
  gap: 4,
});

const LeadsListContainer = styled(InfiniteScroll)({
  display: "flex",
  flexDirection: "column",
  height: "inherit",
  overflow: "auto",
});

const LeadItem = styled(Flex)(({ theme }) => ({
  justifyContent: "space-between",
  boxSizing: "content-box",
  gap: 16,
  height: 46,
  border: "1px solid",
  borderRadius: 4,
  padding: "12px 16px",
  backgroundColor: theme.colors.sysTextWhite,
  borderColor: theme.colors.sysBorderDecorative,
  alignItems: "center",
  transition: smoothTransition,

  "&:hover": {
    backgroundColor: theme.colors.sysPrimarySubtle,
    borderColor: theme.colors.sysPrimaryDefault,
    [`${ActionsContainer}`]: {
      display: "flex",
    },
  },
}));

const ContentFlex = styled(Flex)({
  width: "100%",
  justifyContent: "space-between",
  alignItems: "center",
  overflow: "hidden",
  flex: 1,
});
